import { readTextListPrefix } from '@hector/shared-types';
/**
 * De la forme lue dans le fichier (`lecteurDeForme.ts`) à ce que reçoit l'agent
 * par `canvas_inspect` `formatOf` (#1859) : la cible est résolue par
 * `cibleDeParagraphes.ts`, puis mise au format du reçu partagé
 * (`CanvasFormatReadSchema`).
 *
 * Module pur, comme le lecteur : il ne connaît ni l'éditeur ni le navigateur.
 * La mise au format vit ici, et non dans le lecteur, parce qu'elle est propre
 * à CE consommateur — la vérification d'une écriture ou la copie d'une forme
 * liront la forme complète du lecteur, pas ce résumé borné.
 */

import {
  CANVAS_PARAGRAPH_FIELDS_MAX,
  CANVAS_FORMAT_READ_PARAGRAPH_MAX,
  CANVAS_FORMAT_READ_RUN_TEXT_MAX,
  CANVAS_PARAGRAPH_FORMAT_KEYS,
  CANVAS_RUN_FORMAT_KEYS,
  lieuDeLaCible,
  type CanvasFormatRead,
  type CanvasParagraphFormatKey,
  type CanvasParagraphFormatRead,
  type CanvasParagraphTarget,
  type CanvasRunFormat,
  type CanvasRunFormatKey,
} from '@hector/shared-types';
import { resoudreLaCibleDeParagraphes, type ParagrapheIdentifie } from './cibleDeParagraphes';
import { acteVise, lieuDeLaPartie } from './horsDuCorps';
import type { FormeDeLActe, FormeDeParagraphe, Reglage } from '@hector/docx-forme';
import { pagePourLAgent } from './miseEnPage';
import { stylesPourLAgent } from './stylesPourLAgent';

/**
 * Les réglages de paragraphe à publier : tous, sauf l'épaisseur de bordure
 * d'un paragraphe sans bordure — `border:"none"` dit tout, et il est rendu sur
 * chaque paragraphe d'une lecture.
 */
export function reglagesDeParagrapheARendre(paragraphe: FormeDeParagraphe['paragraphe']): readonly CanvasParagraphFormatKey[] {
  return paragraphe.border.lisible && paragraphe.border.valeur === 'none' ? CANVAS_PARAGRAPH_FORMAT_KEYS.filter((cle) => cle !== 'borderWidthPt') : CANVAS_PARAGRAPH_FORMAT_KEYS;
}

/** Les réglages d'une forme, rangés par origine ; un compartiment vide n'est pas publié. */
function rangerParOrigine<Forme, Cle extends keyof Forme & string>(
  reglages: { [C in Cle]: Reglage<Forme[C]> },
  cles: readonly Cle[],
): {
  direct?: Partial<Pick<Forme, Cle>>;
  style?: Partial<Pick<Forme, Cle>>;
  default?: Partial<Pick<Forme, Cle>>;
  unreadable?: [Cle, ...Cle[]];
} {
  const compartiments: Record<'direct' | 'style' | 'default', Partial<Pick<Forme, Cle>>> = { direct: {}, style: {}, default: {} };
  const nonLisibles: Cle[] = [];
  for (const cle of cles) {
    const reglage = reglages[cle];
    if (reglage.lisible) compartiments[reglage.origine][cle] = reglage.valeur;
    else nonLisibles.push(cle);
  }
  const [premier, ...autres] = nonLisibles;
  return {
    ...(Object.keys(compartiments.direct).length === 0 ? {} : { direct: compartiments.direct }),
    ...(Object.keys(compartiments.style).length === 0 ? {} : { style: compartiments.style }),
    ...(Object.keys(compartiments.default).length === 0 ? {} : { default: compartiments.default }),
    ...(premier === undefined ? {} : { unreadable: [premier, ...autres] }),
  };
}

/**
 * Les passages d'un paragraphe tels que l'agent les lit : deux voisins dont la
 * forme RENDUE est la même n'en font qu'un.
 *
 * WHY : le lecteur distingue aussi ce qui ne se rend pas — une forme directe
 * aux seuls emplacements non lus (`w:rFonts w:cs`, que Word écrit au fil du
 * texte) découpe ses passages, parce qu'une écriture doit savoir l'effacer. La
 * lecture rendue à l'agent n'a pas à grossir pour autant.
 */
function passagesRendus(lu: ParagrapheIdentifie): Array<{ texte: string; forme: ReturnType<typeof rangerParOrigine<CanvasRunFormat, CanvasRunFormatKey>> }> {
  const rendus: ReturnType<typeof passagesRendus> = [];
  let cleDuDernier = '';
  for (const passage of lu.passages) {
    const forme = rangerParOrigine<CanvasRunFormat, CanvasRunFormatKey>(passage, CANVAS_RUN_FORMAT_KEYS);
    const cle = JSON.stringify(forme);
    const dernier = rendus.at(-1);
    if (dernier !== undefined && cle === cleDuDernier) dernier.texte += passage.texte;
    else rendus.push({ texte: passage.texte, forme });
    cleDuDernier = cle;
  }
  return rendus;
}

/**
 * La cible demande un lieu que le document ne porte pas. Le message, en
 * anglais, part tel quel dans le reçu que lit le modèle : il nomme les lieux
 * que le document porte.
 */
export class LieuDeFormeAbsent extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LieuDeFormeAbsent';
  }
}

/** La forme d'un paragraphe telle que l'agent la lit : ses réglages et ceux de chaque passage, rangés par origine. */
export function paragraphePourLAgent(lu: ParagrapheIdentifie): CanvasParagraphFormatRead {
  const textListPrefix = lu.numerotation === 'aucune'
    ? readTextListPrefix(lu.passages.map((passage) => passage.texte).join(''))
    : undefined;
  return {
    ...(textListPrefix === undefined ? {} : { textListPrefix }),
    nodeId: lu.nodeId,
    ...(lu.partie === undefined ? {} : { in: lieuDeLaPartie(lu.partie) }),
    styleId: lu.styleId,
    styleName: lu.styleNom,
    paragraph: rangerParOrigine(lu.paragraphe, reglagesDeParagrapheARendre(lu.paragraphe)),
    runs: passagesRendus(lu).map(({ texte, forme }) => ({
      text: texte.slice(0, CANVAS_FORMAT_READ_RUN_TEXT_MAX),
      ...(texte.length > CANVAS_FORMAT_READ_RUN_TEXT_MAX ? { textLength: texte.length } : {}),
      ...forme,
    })),
    ...(lu.numerosDePage.length === 0 ? {} : { pageNumbers: lu.numerosDePage.slice(0, CANVAS_PARAGRAPH_FIELDS_MAX) }),
    // Ce que le paragraphe porte hors de son texte : l'agent sait AVANT d'écrire qu'une réécriture entière le perdrait.
    ...(lu.horsTexte.length === 0 ? {} : { carries: lu.horsTexte }),
  };
}

/** Ce qui reste à lire quand la cible dépasse la borne d'une lecture, sous la forme d'une cible à repasser. */
function cibleDeLaSuite(cible: CanvasParagraphTarget, restants: readonly ParagrapheIdentifie[]): CanvasParagraphTarget | undefined {
  const premier = restants[0];
  const dernier = restants.at(-1);
  if (premier === undefined || dernier === undefined) return undefined;
  // Une liste n'est pas contiguë : seule la liste des identifiants restants la décrit.
  return cible.kind === 'ids'
    ? { kind: 'ids', nodeIds: restants.map((lu) => lu.nodeId) }
    : { kind: 'range', from: premier.nodeId, to: dernier.nodeId };
}

/**
 * Rend la forme des paragraphes que `cible` désigne, au format du reçu de
 * `canvas_inspect`.
 *
 * Les styles de paragraphe que le fichier définit (`stylesPourLAgent.ts`) ne
 * partent qu'avec la lecture de tout l'acte (`kind: 'all'`), donc avec sa
 * seule première page — sa suite est une plage. WHY : la liste ne dépend pas
 * de la cible et pèse autant qu'une lecture de vingt paragraphes ; rendue à
 * chaque lecture, l'agent la repayait pour un seul paragraphe relu. Le refus
 * d'un style absent nomme les styles définis (`refusDuStyleAbsent`) : c'est le
 * filet d'un agent qui n'a pas fait cette lecture.
 *
 * Avec un lieu (`in`), ce sont les paragraphes de l'en-tête, du pied ou des
 * notes qui sont rendus, chacun avec son lieu.
 *
 * La page de chaque section (`page`) part avec la même lecture de tout l'acte,
 * pour la même raison : elle ne dépend pas de la cible. Un paragraphe qui porte
 * un numéro de page le dit (`pageNumbers`) : ses passages ne le rendent pas.
 *
 * @param entier La forme lue dans le fichier exporté par l'éditeur.
 * @param cible La cible unique demandée par l'agent (`formatOf`).
 * @throws {LieuDeFormeAbsent} si la cible vise un lieu que le document ne porte pas.
 */
export function formePourLAgent(entier: FormeDeLActe, cible: CanvasParagraphTarget): CanvasFormatRead {
  const lieu = lieuDeLaCible(cible);
  const acte = acteVise(entier, lieu);
  if ('absent' in acte) throw new LieuDeFormeAbsent(`${acte.absent} Nothing was modified. Read a place this document has, or its body (no "in").`);
  const { paragraphes, nonResolus, omis } = resoudreLaCibleDeParagraphes(acte, 'cellules_couvertes', cible);
  const [premierNonResolu, ...autresNonResolus] = nonResolus;
  const [premierOmis, ...autresOmis] = omis;
  // Dans un lieu, pas de suite : une liste et une plage ne nomment que le corps. `matchCount` dit ce qui reste, et une note se relit seule.
  const suite = lieu === undefined ? cibleDeLaSuite(cible, paragraphes.slice(CANVAS_FORMAT_READ_PARAGRAPH_MAX)) : undefined;
  return {
    paragraphs: paragraphes.slice(0, CANVAS_FORMAT_READ_PARAGRAPH_MAX).map(paragraphePourLAgent),
    matchCount: paragraphes.length,
    ...(premierOmis === undefined ? {} : { skipped: [premierOmis, ...autresOmis] }),
    ...(premierNonResolu === undefined ? {} : { unresolved: [premierNonResolu, ...autresNonResolus] }),
    ...(suite === undefined ? {} : { continuation: suite }),
    ...(cible.kind === 'all' && lieu === undefined ? stylesPourLAgent(acte) : {}),
    ...(cible.kind === 'all' && lieu === undefined && entier.sections.length > 0 ? { page: pagePourLAgent(entier.sections) } : {}),
  };
}
